import { useEffect, useState } from 'react';
import { isInWebView, hasNativeMethod, callNativeMethod } from './bridge';

/**
 * 主题模式
 * dark = 深色（默认）
 * light = 浅色
 */
export type ThemeMode = 'dark' | 'light';

/** 主题应用到根节点的属性名 */
export const THEME_ATTR = 'data-theme';

/**
 * 解析 VITE_THEME_MODE 环境变量
 * 0 = 深色（强制）
 * 1 = 浅色（强制）
 * 其他值（含 auto / 未配置）= 跟随 App（WebView 中调用 getThemeMode）
 */
function resolveEnvTheme(): ThemeMode | 'auto' {
  const v = import.meta.env.VITE_THEME_MODE;
  if (v === '0') return 'dark';
  if (v === '1') return 'light';
  return 'auto';
}

/**
 * 调用 App 桥接方法 getThemeMode()
 * 返回值：0 = Dark，1 = Light
 * 函数报错 / 无值 / 非 1 一律默认深色（0）
 */
async function getAppThemeMode(): Promise<ThemeMode> {
  try {
    if (isInWebView() && hasNativeMethod('getThemeMode')) {
      const result = await callNativeMethod('getThemeMode');
      const num = Number(result);
      const theme: ThemeMode = Number.isFinite(num) && num === 1 ? 'light' : 'dark';
      console.log('[Bridge] getThemeMode 结果:', result, '→', theme);
      return theme;
    }
  } catch (e) {
    console.error('[Theme] getThemeMode 调用失败，默认深色:', e);
  }
  return 'dark';
}

/**
 * 计算最终主题
 * 优先级：环境变量显式配置 > App 桥接 getThemeMode > 默认深色
 */
export async function resolveTheme(): Promise<ThemeMode> {
  const envTheme = resolveEnvTheme();
  if (envTheme !== 'auto') {
    return envTheme;
  }
  return getAppThemeMode();
}

/**
 * 应用主题：设置根节点 html[data-theme]
 */
export function applyTheme(theme: ThemeMode) {
  document.documentElement.setAttribute(THEME_ATTR, theme);
}

/**
 * 读取当前根节点主题
 */
export function getCurrentTheme(): ThemeMode {
  const current = document.documentElement.getAttribute(THEME_ATTR);
  return current === 'light' ? 'light' : 'dark';
}

/**
 * React Hook：监听根节点 data-theme 变化，返回当前主题
 * 用于组件内根据主题动态切换图标/样式
 */
export function useTheme(): ThemeMode {
  const [theme, setTheme] = useState<ThemeMode>(() => getCurrentTheme());

  useEffect(() => {
    const root = document.documentElement;
    const updateTheme = () => setTheme(getCurrentTheme());

    // 初始化同步一次（main.tsx 异步设置主题完成后，组件可能已渲染）
    updateTheme();

    // 监听 data-theme 属性变化
    const observer = new MutationObserver(() => updateTheme());
    observer.observe(root, { attributes: true, attributeFilter: [THEME_ATTR] });

    return () => observer.disconnect();
  }, []);

  return theme;
}